import React, { forwardRef, useRef } from 'react'

import AntdText, { TextProps } from 'antd/es/typography/Text'
import styled from 'styled-components'

import AutomaticTruncationTooltip from 'src/components/Typography/AutomaticTruncationTooltip'
import { useMergeRefs } from 'src/hooks/use-merge-refs'

import { ColorToken } from '../../constants/theme/types'
import { useTheme } from '../../hooks/use-theme'

export enum TextSize {
  Base = 'Base',
  ExtraSmall = 'ExtraSmall',
  Small = 'Small',
  Large = 'Large',
  ExtraLarge = 'ExtraLarge',
}

export enum TextAlign {
  Left = 'Left',
  Center = 'Center',
  Right = 'Right',
}

type Props = TextProps & {
  size?: TextSize
  colorToken?: ColorToken
  inline?: boolean
  margin?: string
  lineHeight?: string
  maxLines?: number
  allowNewlines?: boolean
  noWrap?: boolean
  pointer?: boolean
  align?: TextAlign
  strikethrough?: boolean
  whiteSpace?: React.CSSProperties['whiteSpace']
  wordBreak?: React.CSSProperties['wordBreak']
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void | Promise<void>
}

export const getFontSize = (size: TextSize) => {
  switch (size) {
    case TextSize.Base:
      return 14
    case TextSize.ExtraSmall:
      return 9
    case TextSize.Small:
      return 12
    case TextSize.Large:
      return 16
    case TextSize.ExtraLarge:
      return 20
  }
}

export const getLineHeight = (size: TextSize) => {
  switch (size) {
    case TextSize.Base:
      return 22
    case TextSize.ExtraSmall:
      return 14
    case TextSize.Small:
      return 20
    case TextSize.Large:
      return 24
    case TextSize.ExtraLarge:
      return 28
  }
}

const getTextAlign = (align: TextAlign) => {
  switch (align) {
    case TextAlign.Left: {
      return 'left'
    }
    case TextAlign.Center: {
      return 'center'
    }
    case TextAlign.Right: {
      return 'right'
    }
  }
}

const makeMaxLinesCss = (maxLines: number) => `
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box !important;
  -webkit-line-clamp: ${maxLines};
  -webkit-box-orient: vertical;
  white-space: normal;
  word-break: break-word;
`

type TextStyleProps = {
  $align?: TextAlign
  $allowNewlines?: boolean
  $color?: string
  $inline?: boolean
  $lineHeight?: string
  $margin?: string
  $maxLines?: number
  $noWrap?: boolean
  $pointer?: boolean
  $size: TextSize
  $strikethrough?: boolean
  $strong?: boolean
  $whiteSpace?: React.CSSProperties['whiteSpace']
  $wordBreak?: React.CSSProperties['wordBreak']
}
const StyledAntdText = styled(AntdText)<TextStyleProps>`
  font-size: ${(props) => getFontSize(props.$size)}px;
  line-height: ${(props) => getLineHeight(props.$size)}px;
  ${(props) => (props.$color ? `color: ${props.$color};` : '')};
  ${(props) => (props.$inline ? `display: inline;` : '')};
  ${(props) => (props.$margin ? `margin: ${props.$margin};` : '')};
  ${(props) => (props.$lineHeight ? `line-height: ${props.$lineHeight};` : '')};
  ${(props) => props.$maxLines && makeMaxLinesCss(props.$maxLines)};
  ${(props) => (props.$allowNewlines ? `white-space: pre-wrap;` : '')};
  ${(props) => (props.$noWrap ? `white-space: nowrap;` : '')};
  ${(props) => (props.$pointer ? `cursor: pointer;` : '')};
  ${(props) => (props.$strong ? `font-weight: bold;` : '')};
  ${(props) => (props.$align ? `text-align: ${getTextAlign(props.$align)};` : '')};
  ${(props) => (props.$strikethrough ? `text-decoration: line-through;` : '')};
  ${(props) => (props.$whiteSpace ? `white-space: ${props.$whiteSpace};` : '')};
  ${(props) => (props.$wordBreak ? `word-break: ${props.$wordBreak};` : '')};
`

/**
 * @deprecated Use govwell-ui Text instead
 */
export const Text = forwardRef<HTMLSpanElement, Props>((props: Props, propRef) => {
  const {
    size = TextSize.Base,
    color,
    colorToken,
    children,
    inline,
    margin,
    lineHeight,
    maxLines,
    id,
    allowNewlines,
    noWrap,
    onClick,
    pointer,
    align = TextAlign.Left,
    strikethrough,
    whiteSpace,
    wordBreak,
    ...rest
  } = props
  const { getTokenVal } = useTheme()
  const { strong } = rest
  const themeColor = getTokenVal(colorToken || 'colorText')
  const internalRef = useRef<HTMLElement>(null)
  const ref = useMergeRefs(propRef, internalRef)

  return (
    <AutomaticTruncationTooltip
      childrenRef={internalRef}
      isEnabled={!!maxLines || whiteSpace === 'nowrap'}
      originalTextChildren={children}
    >
      <StyledAntdText
        onClick={onClick}
        $size={size}
        $color={color || themeColor}
        $inline={inline}
        $margin={margin}
        $lineHeight={lineHeight}
        $maxLines={maxLines}
        $allowNewlines={allowNewlines}
        $noWrap={noWrap}
        $pointer={pointer}
        $align={align}
        $strong={strong}
        $strikethrough={strikethrough}
        $whiteSpace={whiteSpace}
        $wordBreak={wordBreak}
        id={id}
        ref={ref}
        {...rest}
      >
        {children}
      </StyledAntdText>
    </AutomaticTruncationTooltip>
  )
})

export default Text
